import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  selectAreasForCaptureTreeDataSet,
  selectIElement,
  selectIElementChildren,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "./ielements-slice-selector";
import {
  isLightweightArea,
  isLightweightAttachmentsGroup,
  isLightweightDataSetCapture,
  isLightweightImg2d,
  isLightweightImg360,
  isLightweightMarkupOrExternalMarkup,
  isLightweightWithTypeAndHint,
} from "./lightweight-elements-discriminators";
import {
  LightweightElement,
  LightweightElementsRecord,
  LightweightElementsState,
  LightweightElementsStore,
} from "./lightweight-elements-slice";
import { curryAppSelector } from "./reselect";
import { RootState } from "./store";

/**
 * @returns The lightweight elements slice from the app store
 * @param param0 The app store, defined as containing only the lightweight elements slice
 */
export function selectLightweightElementsSlice({
  lightweightElements,
}: LightweightElementsStore): LightweightElementsState {
  return lightweightElements;
}

/**
 * @returns All lightweight elements in the store as a map GUID->element
 * @param param0 The app store, defined as containing only the lightweight elements slice
 */
export function selectLightweightElements({
  lightweightElements,
}: LightweightElementsStore): LightweightElementsRecord {
  return lightweightElements.lightweightElements;
}

/**
 * Walk up the tree of lightweight elements, and select the first ancestor
 * of the given element that matches the predicate
 *
 * @param element Starting LightweightElement
 * @param predicate Function to select the required ancestor
 * @returns The ancestor or undefined if no ancestor matches the predicate
 */
export function selectLightweightAncestor(
  element: LightweightElement | undefined,
  predicate: (el: LightweightElement) => boolean,
) {
  return (state: LightweightElementsStore): LightweightElement | undefined => {
    if (!element) return;
    if (predicate(element)) return element;
    while (element?.parentId) {
      element = selectLightweightElement(element.parentId)(state);
      if (element && predicate(element)) return element;
    }
  };
}

/**
 * Returns the lightweight element with the given ID
 *
 * @param id The ID of the required lightweight element
 * @returns The lightweight element with the given ID, or undefined if not found
 */
export function selectLightweightElement(id: GUID | undefined) {
  return ({
    lightweightElements,
  }: LightweightElementsStore): LightweightElement | undefined => {
    if (!id) return;
    return lightweightElements.lightweightElements[id];
  };
}

/** Loading state of an ielement */
export enum IElementCompleteResult {
  /** The ielement is fully loaded for a given GUID */
  IElementComplete = "IElementComplete",
  /** Only the lightweight element is available for a given GUID */
  OnlyLightweightAvailable = "OnlyLightweightAvailable",
  /** Neither the ielement nor the lightweight element is available for a given GUID */
  IElementNotFound = "IElementNotFound",
}

/**
 * @returns whether the element with the given ID is fully loaded, partially loaded, or not found.
 * @param state The current state of the app store
 * @param id The ID of the element to check
 */
export const selectIsIElementComplete = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElements,
      (state: RootState, id: GUID | undefined) => id,
    ],
    computeIsIElementComplete,
  ),
);

/**
 * @returns Whether any children are missing for the given element
 * @param iElement The iElement to check
 * @param state The current state of the app store
 */
export function areChildrenMissing(
  iElement: IElement,
  state: OnlyIElementsStore,
): boolean {
  if (iElement.childrenIds?.length && !isIElementAreaSection(iElement)) {
    const children = selectIElementChildren(iElement.id)(state);
    return children.length !== iElement.childrenIds.length;
  }
  return false;
}

/**
 * @returns Whether any ancestor are missing for the given element
 * @param iElement The iElement to check
 * @param state The current state of the app store
 */
export function areAncestorsMissing(
  iElement: IElement,
  state: RootState,
): boolean {
  let parent = iElement.parentId
    ? state.lightweightElements.lightweightElements[iElement.parentId]
    : undefined;
  while (parent) {
    if (!state.iElements.iElements[parent.id]) {
      return true;
    }
    parent = parent.parentId
      ? state.lightweightElements.lightweightElements[parent.parentId]
      : undefined;
  }

  return false;
}

function computeIsIElementComplete(
  iElements: IElementsState,
  lightweightElements: LightweightElementsRecord,
  id: GUID | undefined,
): IElementCompleteResult {
  if (!id) return IElementCompleteResult.IElementNotFound;

  const state = { iElements };
  const iElement = selectIElement(id)(state);
  if (iElement && !areChildrenMissing(iElement, state)) {
    return IElementCompleteResult.IElementComplete;
  }

  if (lightweightElements[id]) {
    return IElementCompleteResult.OnlyLightweightAvailable;
  }

  return IElementCompleteResult.IElementNotFound;
}

/**
 * @returns The first descendant lightweight element matching the predicate
 * @param element The element whose descendant should be checked
 * @param predicate The function to discriminate the desired child
 */
export function selectLightweightChild<Type extends LightweightElement>(
  element: LightweightElement | undefined,
  predicate: (el: LightweightElement) => el is Type,
) {
  return ({
    lightweightElements,
  }: LightweightElementsStore): LightweightElement | undefined => {
    if (!element) return;
    if (!element.childrenIds) return;

    // A stack of elements to check with their depth from the starting element
    const stack: LightweightElement[] = [element];

    while (stack.length > 0) {
      // Pop the element and depth from the stack
      const toCheck = stack.pop();
      if (!toCheck) continue;
      const currentElement = toCheck;

      // Check the search and early stop conditions
      if (predicate(currentElement)) {
        return currentElement;
      }

      // Don't compute children if the depth is already at the max allowed depth
      if (!currentElement.childrenIds) {
        continue;
      }

      // Check for children in reverse order to maintain order when we pop from the stack
      for (let idx = currentElement.childrenIds.length - 1; idx >= 0; --idx) {
        const child =
          lightweightElements.lightweightElements[
            currentElement.childrenIds[idx]
          ];
        if (child) {
          stack.push(child);
        }
      }
    }
  };
}

/**
 *
 * @param element The element whose children to search
 * @param predicate The predicate that the children should match
 * @returns All the direct children of the given element that match the given predicate
 */
export function selectLightweightDirectChildren(
  element: LightweightElement | undefined,
  predicate: (el: LightweightElement) => boolean,
) {
  return ({
    lightweightElements,
  }: LightweightElementsStore): LightweightElement[] => {
    if (!element) return [];
    if (!element.childrenIds) return [];

    const result: LightweightElement[] = [];
    for (const childId of element.childrenIds) {
      const child = lightweightElements.lightweightElements[childId];
      if (child && predicate(child)) {
        result.push(child);
      }
    }
    return result;
  };
}

/**
 * The state of the app store relevant for the project elements selectors, including both the iElements and lightweight elements slices
 */
type ProjectElementsState = {
  /** The iElements slice of the app store */
  iElements: IElementsState;
  /** The lightweightElements slice of the app store */
  lightweightElements: LightweightElementsState;
};

/**
 * @returns The ancestor area element of the given element, if any.
 * @param state The current state of the app store
 * @param id The ID of the element whose ancestor area should be found
 */
export const selectAncestorArea = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      selectIElementsSlice,
      (state: ProjectElementsState, id: GUID | undefined) => id,
    ],
    computeAncestorArea,
  ),
);

function computeAncestorArea(
  lightweightElements: LightweightElementsState,
  iElements: IElementsState,
  id: GUID | undefined,
): IElement | undefined {
  if (!id) return;

  const element = lightweightElements.lightweightElements[id];

  const parent = selectLightweightAncestor(
    element,
    (el) => isLightweightArea(el) || isLightweightDataSetCapture(el),
  )({ lightweightElements });

  if (!parent) return;

  if (isLightweightArea(parent)) {
    return selectIElement(parent.id)({ iElements });
  }

  // If the parent is a dataset in the capture tree, find its parent area
  const parentAreas = selectAreasForCaptureTreeDataSet(
    { iElements },
    parent.id,
  );
  if (parentAreas.length === 0) return;
  return selectIElement(parentAreas[0])({ iElements });
}

/**
 * @param state the current state of the app store
 * @returns The date of the last capture in the lightweight elements store, or undefined if there are no captures
 */
export const selectLastCaptureDate = createSelector(
  [selectLightweightElements],
  computeLastCaptureDate,
);

function computeLastCaptureDate(
  lightweightElements: LightweightElementsRecord,
): Date | undefined {
  let latestDate: Date | undefined = undefined;
  for (const element of Object.values(lightweightElements)) {
    if (!element || !isRelevantForCaptureDate(element, lightweightElements)) {
      continue;
    }
    const date = new Date(element.createdAt);
    if (!latestDate || date > latestDate) {
      latestDate = date;
    }
  }
  return latestDate;
}

/**
 * Only navigable captures contribute to the latest capture date, not photogrammetry
 * source photos or annotation images.
 *
 * @returns True if the element's creation date should count as a capture date
 * @param element The element to check
 * @param lightweightElements All lightweight elements, to walk the element's ancestors
 */
export function isRelevantForCaptureDate(
  element: LightweightElement,
  lightweightElements: LightweightElementsRecord,
): boolean {
  if (isLightweightImg360(element)) return true;

  // Use the navigable orthomosaic's scan date, not its source photos' dates.
  if (
    isLightweightWithTypeAndHint(
      element,
      IElementType.section,
      IElementTypeHint.photogrammetryScan,
    )
  ) {
    return true;
  }

  if (isLightweightImg2d(element)) {
    // A photo counts only as a capture at a location, which means sitting directly inside a
    // `group/attachments`. This excludes photogrammetry source images, which hang off a
    // `section/photogrammetryPhotos` instead.
    const parent = element.parentId
      ? lightweightElements[element.parentId]
      : undefined;
    if (!parent || !isLightweightAttachmentsGroup(parent)) return false;

    // Annotations keep their photos under a `group/attachments` too, so the ancestor chain
    // still has to be checked: Holobuilder markups hold an img2d directly, Sphere XG ones
    // nest it one level deeper.
    return !hasMarkupAncestor(element, lightweightElements);
  }

  return false;
}

/**
 * @returns True if any ancestor of the element is a markup or an external markup
 * @param element The element whose ancestors to walk
 * @param lightweightElements All lightweight elements, to look ancestors up
 */
function hasMarkupAncestor(
  element: LightweightElement,
  lightweightElements: LightweightElementsRecord,
): boolean {
  let current: LightweightElement | undefined = element;
  while (current?.parentId) {
    current = lightweightElements[current.parentId];
    if (current && isLightweightMarkupOrExternalMarkup(current)) return true;
  }
  return false;
}

// Collect all descendants of an iElement that satisfy the input predicate
export const selectLightweightChildren = curryAppSelector(
  createSelector(
    [
      (state: LightweightElementsState) => state.lightweightElements,
      (state: LightweightElementsState, element: LightweightElement) => element,
      (
        state: LightweightElementsState,
        element: LightweightElement,
        predicate: (e: LightweightElement) => boolean,
      ) => predicate,
    ],
    (
      lightweightElements: LightweightElementsRecord,
      element: LightweightElement,
      predicate: (e: LightweightElement) => boolean,
    ): LightweightElement[] => {
      const elements: LightweightElement[] = [];

      const stack = [element];
      while (stack.length > 0) {
        const currentElement = stack.pop();

        if (!currentElement) break;

        if (predicate(currentElement)) {
          elements.push(currentElement);
        }

        if (currentElement.childrenIds) {
          for (const childId of currentElement.childrenIds) {
            const child = lightweightElements[childId];
            if (!child) continue;

            stack.push(child);
          }
        }
      }

      return elements;
    },
  ),
);
